// Management Accounting — Cost Centres Master (Phase 1)
// Per BRD §2.6 + FRS §2.6. Cost Centre is the orthogonal "WHERE"
// dimension — independent of operational Branch. CRUD writes to
// mca_cost_centres only (Policy F).
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card } from "@/components/ui-helpers";
import { Plus, X, Pencil, Archive, ArchiveRestore, Search, Building } from "lucide-react";
import { inputCls, Field } from "../Projects";
import { toast, Toaster } from "sonner";

export default function MaCostCentresPage() {
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (statusFilter !== "all") q.set("status", statusFilter);
      if (search.trim()) q.set("search", search.trim());
      const { data } = await api.get(`/mca/cost-centres?${q.toString()}`);
      setList(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load cost centres");
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [statusFilter]);

  async function archive(row) {
    if (!window.confirm(`Archive cost centre "${row.name}"? It will no longer be available for new classifications.`)) return;
    try {
      await api.post(`/mca/cost-centres/${row.id}/archive`);
      toast.success("Archived");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Archive failed");
    }
  }

  async function unarchive(row) {
    try {
      await api.post(`/mca/cost-centres/${row.id}/unarchive`);
      toast.success("Restored to active");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unarchive failed");
    }
  }

  return (
    <div data-testid="mca-cost-centres-page">
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow="Management Accounting · Phase 1"
        title="Cost Centres"
        subtitle="Organisational structure dimension — captures WHERE in the company a cost was incurred (Head Office, HR, Finance, etc.). Used alongside Management Categories to slice management reports by department and by expense nature."
        actions={
          <button data-testid="mca-cost-centres-add-btn"
                  onClick={() => { setEditing({ is_active: true }); setShowForm(true); }}
                  className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
            <Plus size={14} /> Add Cost Centre
          </button>
        }
      />

      <Card className="mb-4 p-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px] relative">
          <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Search</label>
          <Search size={14} className="absolute left-3 bottom-3 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
                 onKeyDown={e => e.key === "Enter" && load()}
                 placeholder="Code, name, description…"
                 className="w-full pl-9 pr-3 py-2.5 border border-slate-300 text-sm bg-white focus:border-[#F97316] focus:outline-none"
                 data-testid="mca-cost-centres-search-input" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Status</label>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                  className="px-3 py-2.5 border border-slate-300 text-sm bg-white"
                  data-testid="mca-cost-centres-status-filter">
            <option value="active">Active only</option>
            <option value="archived">Archived only</option>
            <option value="all">All</option>
          </select>
        </div>
        <button onClick={load} className="px-3 py-2.5 border border-slate-300 text-xs font-bold uppercase">
          Apply
        </button>
        <div className="text-xs text-slate-500 ml-auto">Total: {list.length}</div>
      </Card>

      <Card className="overflow-hidden">
        <table className="w-full" data-testid="mca-cost-centres-table">
          <thead className="bg-slate-50 border-b-2 border-slate-200 sticky top-0 z-10">
            <tr>
              <th className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-4">Code</th>
              <th className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-4">Name</th>
              <th className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-4">Parent</th>
              <th className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-4">Description</th>
              <th className="text-center text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-4">Status</th>
              <th className="text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="p-10 text-center text-slate-500 text-sm">Loading…</td></tr>
            )}
            {!loading && list.length === 0 && (
              <tr><td colSpan={6} className="p-10 text-center text-slate-500 text-sm">
                <Building size={28} className="inline-block mb-3 text-slate-300" />
                <div>No cost centres match the current filters.</div>
              </td></tr>
            )}
            {list.map(row => (
              <tr key={row.id} className="border-b border-slate-100 hover:bg-slate-50/50"
                  data-testid={`mca-cost-centres-row-${row.code}`}>
                <td className="py-3 px-4 text-sm font-bold text-slate-900">{row.code}</td>
                <td className="py-3 px-4 text-sm text-slate-900">{row.name}</td>
                <td className="py-3 px-4 text-sm text-slate-600">{row.parent_name || "—"}</td>
                <td className="py-3 px-4 text-xs text-slate-600 max-w-md truncate" title={row.description}>{row.description || "—"}</td>
                <td className="py-3 px-4 text-center">
                  {row.is_active ? (
                    <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 bg-emerald-100 text-emerald-800">Active</span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 bg-slate-200 text-slate-700">Archived</span>
                  )}
                </td>
                <td className="py-3 px-4 text-right">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => { setEditing(row); setShowForm(true); }}
                            className="text-slate-500 hover:text-[#F97316] p-1"
                            title="Edit"
                            data-testid={`mca-cost-centres-row-edit-${row.code}`}>
                      <Pencil size={14} />
                    </button>
                    {row.is_active ? (
                      <button onClick={() => archive(row)} className="text-slate-500 hover:text-amber-700 p-1"
                              title="Archive"
                              data-testid={`mca-cost-centres-row-archive-${row.code}`}>
                        <Archive size={14} />
                      </button>
                    ) : (
                      <button onClick={() => unarchive(row)} className="text-slate-500 hover:text-emerald-700 p-1"
                              title="Restore">
                        <ArchiveRestore size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {showForm && (
        <CostCentreForm record={editing}
                        allCostCentres={list}
                        onClose={() => { setShowForm(false); load(); }} />
      )}
    </div>
  );
}

function CostCentreForm({ record, allCostCentres, onClose }) {
  const [form, setForm] = useState(record || {});
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const parentOptions = allCostCentres.filter(c =>
    c.is_active && !(c.parent_id || "").trim() && c.id !== form.id
  );

  async function save(e) {
    e.preventDefault();
    if (!(form.name || "").trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        code: (form.code || "").trim(),
        name: (form.name || "").trim(),
        parent_id: (form.parent_id || "").trim(),
        description: (form.description || "").trim(),
        is_active: form.is_active !== false,
      };
      if (form.id) await api.put(`/mca/cost-centres/${form.id}`, payload);
      else await api.post(`/mca/cost-centres`, payload);
      toast.success(form.id ? "Updated" : "Created");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-2xl" data-testid="mca-cost-centres-modal">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold">
            {form.id ? "Edit Cost Centre" : "New Cost Centre"}
          </h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={save} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Field label="Code">
              <input value={form.code || ""} onChange={e => set("code", e.target.value)}
                     placeholder="Auto-generated if left blank (e.g. CC-011)"
                     className={inputCls}
                     data-testid="mca-cost-centres-modal-code-input" />
            </Field>
          </div>
          <div>
            <Field label="Name *">
              <input value={form.name || ""} onChange={e => set("name", e.target.value)}
                     placeholder="e.g. HR / Finance / IT"
                     className={inputCls}
                     data-testid="mca-cost-centres-modal-name-input" />
            </Field>
          </div>
          <div>
            <Field label="Parent (optional)">
              <select value={form.parent_id || ""} onChange={e => set("parent_id", e.target.value)}
                      className={inputCls}
                      data-testid="mca-cost-centres-modal-parent-dropdown">
                <option value="">(top-level — no parent)</option>
                {parentOptions.map(p => (
                  <option key={p.id} value={p.id}>{p.code} — {p.name}</option>
                ))}
              </select>
              <div className="mt-1 text-[10px] text-slate-500">Phase 1 supports max 2-level hierarchy.</div>
            </Field>
          </div>
          <div className="md:col-span-2">
            <Field label="Description">
              <textarea rows={3} value={form.description || ""} onChange={e => set("description", e.target.value)}
                        placeholder="Plain-English description of what this Cost Centre captures."
                        className={inputCls}
                        data-testid="mca-cost-centres-modal-description-textarea" />
            </Field>
          </div>
          <div className="md:col-span-2 flex justify-end gap-2 pt-3 border-t border-slate-100">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-slate-300 text-xs font-bold uppercase">
              Cancel
            </button>
            <button disabled={saving} type="submit" data-testid="mca-cost-centres-modal-save-btn"
                    className="px-4 py-2 bg-[#F97316] text-white text-xs font-bold uppercase disabled:opacity-50">
              {saving ? "Saving…" : (form.id ? "Update" : "Create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
